import {ExperienceLevel, Profile, ProfileApi, Skill} from "@betrue/shared/interfaces";
import {gql, makeVar, useLazyQuery, useMutation} from "@apollo/client";
import {useState} from 'react';
import {BASE_ENTITY_FIELDS} from "./base-fields";
import {ReactiveVar} from "@apollo/client/cache/inmemory/reactiveVars";
import {useReactiveResult, useResult} from "./results";

const CORE_JOB_FIELDS = gql`
  fragment CoreJobFields on JobModel {
    ${BASE_ENTITY_FIELDS}
    baseSkills {
      key
      label
    }
    category {
      id
    }
    label
  }
`
const CORE_SKILL_FIELDS = gql`
  fragment CoreSkillFields on SkillModel {
    ${BASE_ENTITY_FIELDS}
    key
    label
    usageCount
  }
`

const CORE_PROFILE_SKILL_FIELDS = gql`
  ${CORE_SKILL_FIELDS}
  fragment CoreProfileSkillFields on ProfileSkillModel {
    ${BASE_ENTITY_FIELDS}
    experienceLevel
    skill {
      ...CoreSkillFields
    }
  }
`

const CORE_PROFILE_FIELDS = gql`
  ${CORE_JOB_FIELDS}
  ${CORE_PROFILE_SKILL_FIELDS}
  fragment CoreProfileFields on ProfileModel {
    ${BASE_ENTITY_FIELDS}
    available
    portfolio
    description
    publicConsent
    internalConsent
    completion
    price
    rating
    experienceLevel
    job {
      ...CoreJobFields
    }
    skills {
      ...CoreProfileSkillFields
    }

  }
`

const REMOVE_JOB_FROM_PROFILE = gql`
  ${CORE_PROFILE_FIELDS}
  mutation RemoveJobFromProfile($profileId: String!){
    removeJobFromProfile(profileId: $profileId){
      ...CoreProfileFields
    }
  }
`

const UPDATE_PROFILE = gql`
  ${CORE_PROFILE_FIELDS}
  mutation UpdateProfile($profile: ProfileModelInput!){
    updateProfile(profile: $profile) {
      ...CoreProfileFields
    }

  }
`

const ADD_JOB_TO_PROFILE = gql`
  ${CORE_PROFILE_FIELDS}
  mutation AddJobToProfile($jobId: String!, $profileId: String!){
    addJobToProfile(jobId: $jobId, profileId: $profileId){
      ...CoreProfileFields
    }
  }
`

const ADD_SKILL_TO_PROFILE = gql`
  ${CORE_PROFILE_FIELDS}
  mutation AddSkillToProfile($profileId: String!,$skill: SkillModelInput!){
    addSkillToProfile(profileId: $profileId, skill: $skill) {
      ...CoreProfileFields
    }
  }
`

const REMOVE_SKILL_FROM_PROFILE = gql`
  ${CORE_PROFILE_FIELDS}
  mutation RemoveSkillFromProfile($profileId: String!,$skill: SkillModelInput!){
    removeSkillFromProfile(profileId: $profileId, skill: $skill) {
      ...CoreProfileFields
    }
  }
`
const UPDATE_PROFILE_SKILL = gql`
  ${CORE_PROFILE_FIELDS}
  mutation UpdateProfileSkill($experience: ExperienceLevel!, $profileId: String!, $skill: SkillModelInput!) {
    updateProfileSkill(experience : $experience, profileId: $profileId, skill: $skill) {
      ...CoreProfileFields
    }
  }
`

const UPDATE_PROFILE_RATING = gql`
  ${CORE_PROFILE_FIELDS}
  mutation UpdateProfileRating($profileId: String!, $rating: Float!) {
    updateProfileRating(rating:$rating,profileId: $profileId) {
      ...CoreProfileFields
    }
  }
`

const UPDATE_PUBLIC_STATE = gql`
  ${CORE_PROFILE_FIELDS}
  mutation UpdatePublicState($profileId: String!, $isPublic: Boolean!) {
    updatePublicState(isPublic: $isPublic, profileId: $profileId) {
      ...CoreProfileFields
    }
  }
`

const GET_PROFILE_BY_ID = gql`
  ${CORE_PROFILE_FIELDS}
  query GetProfileById($id: String!){
    getByProfileById(id: $id) {
      ...CoreProfileFields
    }
  }
`


const GET_ALL_PROFILES_PARTIALS = gql`
  query GetAllProfiles{
    getAllProfiles {
      ${BASE_ENTITY_FIELDS}
      completion
      job {
        label
      }
      rating
      price
      skills {
        ${BASE_ENTITY_FIELDS}
        skill {
          id
          key
          label
        }
      }
      person {
        ${BASE_ENTITY_FIELDS}
        firstName
        lastName
      }
    }
  }
`

const GET_ALL_PROFILES_FULL = gql`

  query GetAllProfiles{
    getAllProfiles {
      ${BASE_ENTITY_FIELDS}
      price
      completion
      rating
      job {
        label
      }
      skills {
        ${BASE_ENTITY_FIELDS}
        skill {
          id
          key
          label
        }
        experienceLevel
      }
      person {
        ${BASE_ENTITY_FIELDS}
        firstName
        lastName
      }
    }
  }
`

const GET_ALL_PUBLIC_PROFILES = gql`
  query GetAllPublicProfiles{
    getPublicProfiles {
      ${BASE_ENTITY_FIELDS}
      description
      person {
        ${BASE_ENTITY_FIELDS}
        firstName
      }
      job {
        label
      }
      skills {
        skill {
          key
          label
        }
      }
    }
  }
`
export const currentProfile = makeVar(null)
export const profileList = makeVar(null)


export function useProfile<T = Profile | Profile[]>(store?: ReactiveVar<T>): [T, ProfileApi] {

  const [result, setResult] = store ? useReactiveResult(store) : useState<T>()
  const getProfileById =  useResult(useLazyQuery(GET_PROFILE_BY_ID,
    {fetchPolicy: 'cache-and-network'}), setResult)
  const addSkill = useResult(useMutation(ADD_SKILL_TO_PROFILE,), setResult)
  const addJob = useResult(useMutation(ADD_JOB_TO_PROFILE,), setResult)
  const updateProfile = useResult(useMutation(UPDATE_PROFILE, {fetchPolicy: "network-only"}), setResult)
  const removeJob = useResult(useMutation(REMOVE_JOB_FROM_PROFILE, {fetchPolicy: "network-only"}), setResult)
  const updateProfileRating = useResult(useMutation(UPDATE_PROFILE_RATING, {fetchPolicy: "network-only"}), setResult)
  const removeSkill = useResult(useMutation(REMOVE_SKILL_FROM_PROFILE, {fetchPolicy: "network-only"}), setResult)
  const updateProfileSkill = useResult(useMutation(UPDATE_PROFILE_SKILL, {fetchPolicy: "network-only"}), setResult)
  const getAllProfiles = useResult(useLazyQuery(GET_ALL_PROFILES_PARTIALS, {fetchPolicy: "network-only"}), setResult)
  const getAllPublicProfiles = useResult(useLazyQuery(GET_ALL_PUBLIC_PROFILES, {fetchPolicy: "network-only"}), setResult)
  const updatePublicState = useResult(useMutation(UPDATE_PUBLIC_STATE, {fetchPolicy: "network-only"}), setResult)

  return [
    result, {
      addSkillToProfile(s: Skill, profileId: string) {
        addSkill({
          variables: {
            skill: s,
            profileId: profileId
          }
        })
      },
      createProfile(Profile) {
        console.log('not implemented')
      },
      getProfileById(id: string) {
        getProfileById({
          variables: {
            id: id
          }
        })
      },
      removeJobFromProfile(profileId) {
        removeJob({
          variables: {
            profileId: profileId
          }
        })
      },
      removeSkillFromProfile(s: Skill, profileId: string) {
        removeSkill({
          variables: {
            skill: s,
            profileId: profileId
          }
        })
      },
      updatePublicState(profileId: string, isPublishable: boolean) {
        updatePublicState({
          variables: {
            profileId: profileId,
            isPublic: isPublishable
          }
        })
      },
      updateProfile(p: Profile) {
        updateProfile({
          variables: {
            profile: p
          }
        })
      },
      updateProfileRating(rating: Profile["rating"], profileId: string) {
        updateProfileRating({
          variables: {
            rating: rating,
            profileId: profileId,
          }
        })
      }
      ,
      updateProfileSkill(s: Skill, profileId: string, xp: ExperienceLevel) {
        updateProfileSkill({
          variables: {
            skill: s,
            profileId: profileId,
            experience: xp
          }
        })

      },
      addJobToProfile: (jobId, profileId) => {
        addJob({
          variables: {
            jobId: jobId,
            profileId: profileId
          }
        })
      },
      getPublicProfiles() {

        getAllPublicProfiles()

      },
      getAllProfiles: () => {
        getAllProfiles()
      }
    }]
}
